import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '@app/components/common/Modal/Modal';
import { IDealRoomPeople } from '@app/interfaces/deal-room-people';
import { useTranslation } from 'react-i18next';
import { List } from 'antd';
import * as S from './DealRoomSmsModal.styles';
import { getDealRoomFirstBuildingText, getPeopleFullName, getUserFullName } from '@app/utils/utils';
import { notificationController } from '@app/controllers/notificationController';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { IDealRoom, IDealRoomShare } from '@app/interfaces/deal-room';
import { IClient } from '@app/interfaces/client';
import { IUser } from '@app/interfaces/user';
import { sendSms } from '@app/api/deal-room.api';
import { DealRoomUserItem } from '../DealRoomWhatsAppModal/DealRoomWhatsAppModal';

interface DealRoomSmsModalProps {
  dealRoom: IDealRoomShare;
  shortLink?: string | null;
  open: boolean;
  people: IDealRoomPeople[];
  selectedPeople?: string | null;
  onClose: () => void;
}

export const DealRoomSmsModal: React.FC<DealRoomSmsModalProps> = ({
  dealRoom,
  shortLink,
  open,
  people,
  selectedPeople,
  onClose,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [form] = BaseForm.useForm();
  const [previewMessage, setPreviewMessage] = useState('');

  const onOk = () => {
    form.submit();
  };

  const toggleCheck = (field: string, id: string) => {
    const currentSelected: string[] = form.getFieldValue(field) || [];
    if (currentSelected.includes(id)) {
      form.setFieldsValue({ [field]: currentSelected.filter((x) => x !== id) });
    } else {
      form.setFieldsValue({ [field]: [...currentSelected, id] });
    }
    calculatePreviewMessage();
  };

  const onSubmit = async (values: { to: string[] }) => {
    if (values.to.length === 0) {
      notificationController.error({ message: t('dealRooms.landing.pleaseSelectAtLeastOneUser') });
      return;
    }
    setLoading(true);
    try {
      await sendSms(dealRoom.id as string, {
        members: people
          .filter((p) => values.to.includes(p.id))
          .map((p) => ({
            id: p.id,
            name: getPeopleFullName(p),
            phone: p.phone as string,
          })),
      });
      notificationController.success({ message: t('dealRooms.landing.smsMessageSentSuccessfully') });
      onClose();
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const getChecked = (field: string, id: string): boolean => {
    const currentSelected: string[] = form.getFieldValue(field) || [];
    return currentSelected.includes(id);
  };

  const onSelectAll = () => {
    form.setFieldsValue({ to: people.map((p) => p.id).filter((p) => p) });
    calculatePreviewMessage();
  };

  const onClearAll = () => {
    form.setFieldsValue({ to: [] });
    calculatePreviewMessage();
  };

  const calculatePreviewMessage = useCallback(() => {
    let message = `${(dealRoom.client as IClient)?.id_num || ''}: ${getDealRoomFirstBuildingText(
      dealRoom as IDealRoom,
    )} ,עדכון מידע<br/>`;
    const firstPeople = people.find((p) => (form.getFieldValue('to') || []).includes(p.id));
    if (firstPeople) {
      message += `<br/>${getPeopleFullName(firstPeople)} שלום,`;
    }
    if (dealRoom.responsible) {
      message += `<br/>${getUserFullName(
        dealRoom.responsible as IUser,
      )} עדכן/נה את האזור האישי, ניתן להיכנס לאזור האישי בלינק הבא: ${shortLink || ''}`;
    }
    message += `<br/><br/>בברכה,`;
    message += `<br/>בר-עוז המרכז לביטוח`;
    setPreviewMessage(message);
  }, [dealRoom, form, people, shortLink]);

  useEffect(() => {
    if (selectedPeople) {
      form.setFieldsValue({ to: [selectedPeople] });
    } else {
      form.setFieldsValue({ to: people.map((p) => p.id).filter((p) => p) });
    }
  }, [form, people, selectedPeople]);

  useEffect(() => {
    calculatePreviewMessage();
  }, [calculatePreviewMessage]);

  return (
    <Modal
      size="large"
      open={open}
      title={t('dealRooms.landing.sendSmsMessage')}
      onCancel={onClose}
      onOk={() => onOk()}
      okText={t('dealRooms.landing.send')}
      cancelText={t('common.cancel')}
      closable={true}
      maskClosable={false}
      destroyOnClose
      confirmLoading={loading}
    >
      <S.BaseFormWrapper
        form={form}
        layout="vertical"
        name="dealRoomSmsForm"
        onFinish={onSubmit}
        style={{ width: '100%' }}
      >
        <BaseForm.Item
          name="to"
          rules={[{ required: true, message: t('dealRooms.landing.toRequired') }]}
          label={
            <S.FlexWrapper>
              <span>{t('dealRooms.landing.to')}</span>
              <S.ActionWrapper>
                <S.Link onClick={onSelectAll}>{t('dealRooms.landing.selectAll')}</S.Link>|
                <S.Link onClick={onClearAll}>{t('dealRooms.landing.clearAll')}</S.Link>
              </S.ActionWrapper>
            </S.FlexWrapper>
          }
        >
          <List
            grid={{ gutter: 16, column: 2 }}
            dataSource={people}
            renderItem={(item) => (
              <DealRoomUserItem
                item={item}
                key={item.id}
                checked={getChecked('to', item.id)}
                toggleCheck={(id) => toggleCheck('to', id)}
              />
            )}
          />
        </BaseForm.Item>
        <S.PreviewContainer>
          <S.PreviewWrapper>
            <S.PreviewHeader>{t('dealRooms.landing.smsMessagePreview')}</S.PreviewHeader>
          </S.PreviewWrapper>
          <S.PreviewDescription dangerouslySetInnerHTML={{ __html: previewMessage }} />
        </S.PreviewContainer>
      </S.BaseFormWrapper>
    </Modal>
  );
};
